import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MainTableFilterModel,
  PrintReportFilterModel,
} from 'src/app/shared/models/main-table-filter.model';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from 'src/environments/environment';
import { FormDataService } from 'src/app/shared/attachments/service/form-data.service';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import {
  IApiResponseGroupMedicines,
  IMedicineRequest,
  IMedicineResponse,
} from '../models/medicine.model';
import { MedicineEvent } from '../enums/medicine-event.enum';
import { IUseMedicine } from '../models/use-medicine.model';
import { IMedicineStock } from '../models/medicine-stock.model';

const COLUMN_TO_KEY_MAP: any = {
  refrigerated: 'ShouldBeRefrigerated',
  quantityType: 'QuantityType.Name',
  medicineCategory: 'Category.Name',
  minQuantity: 'MinimumQuantity',
  responsiblePositions: 'ResponsibleForPositions.Name',
  responsiblePositionGroups: 'ResponsibleForPositionGroups.Name',
  responsibleRoles: 'ResponsibleForRoles.Name',
};
const OR_GROUP_MAP: Record<string, string> = {
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
};
@Injectable({
  providedIn: 'root',
})
export class MedicineApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/medicine`;
  }

  constructor(private http: HttpClient) {}

  getPagedMedicines(
    filter?: MainTableFilterModel,
    menuId?: string
  ): Observable<IApiResponseGroupMedicines> {
    let params = createFilterParams(filter);
    if (menuId) {
      params = params.set('MenuId', menuId);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<IApiResponseGroupMedicines>(this.url, { params });
  }

  getAllMedicines(): Observable<IMedicineResponse[]> {
    const url = `${this.url}/List`;
    return this.http.get<IMedicineResponse[]>(url);
  }

  getMedicineById(medicineId: string): Observable<IMedicineResponse> {
    const url = `${this.url}/${medicineId}`;
    return this.http.get<IMedicineResponse>(url);
  }

  createMedicine(medicine: IMedicineRequest): Observable<IMedicineResponse> {
    return this.http.post<IMedicineResponse>(this.url, medicine, { headers: this.headers });
  }

  updateMedicine(medicine: IMedicineRequest): Observable<IMedicineResponse> {
    return this.http.put<IMedicineResponse>(this.url, medicine, { headers: this.headers });
  }

  deleteMedicine(id: string) {
    const url = `${this.url}/${id}`;
    return this.http.delete<IMedicineResponse>(url);
  }

  useMedicine(medicine: IUseMedicine): Observable<IMedicineResponse> {
    const formData = FormDataService.mapToFormData(medicine);
    const url = `${this.url}/${medicine.id}/${MedicineEvent.Use}`;
    return this.http.put<IMedicineResponse>(url, formData);
  }

  addtoStock(medicine: IUseMedicine): Observable<IMedicineResponse> {
    const formData = FormDataService.mapToFormData(medicine);
    const url = `${this.url}/${medicine.id}/${MedicineEvent.Add}`;
    return this.http.put<IMedicineResponse>(url, formData);
  }

  adjustStock(medicine: IUseMedicine): Observable<IMedicineResponse> {
    const formData = FormDataService.mapToFormData(medicine);
    const url = `${this.url}/${medicine.id}/${MedicineEvent.Adjust}`;
    return this.http.put<IMedicineResponse>(url, formData);
  }

  getMedicineStock(
    medicineId: string,
    currentFilter?: MainTableFilterModel
  ): Observable<IMedicineStock[]> {
    const url = `${this.url}/${medicineId}/Stock`;
    const params = createFilterParams(currentFilter);

    return this.http.get<IMedicineStock[]>(url, { params });
  }

  printReport(filter?: PrintReportFilterModel, menuId?: string): Observable<Blob> {
    const url = `${this.url}/Report`;
    let params = createFilterParams(filter);

    if (menuId) {
      params = params.set('MenuId', menuId);
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    return this.http.get(url, { params, responseType: 'blob' });
  }
}
